import { Box, IconButton, Tab, Tabs, TextField } from '@mui/material';
import { Button } from '@mui/material';
import UpAndDownFilterIcon from 'assets/images/iPrise/UpAndDownFilter.svg';
import { useDebounce } from 'hooks';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';

import AddAndEditJobV2 from '../../../common/AddAndEditJobV2';
import SortMenu from './SortMenu';

const JobQueueHeader: React.FC = () => {
  const [openAddJob, setOpenAddJob] = useState(false);
  const { supportJobQueueService } = useStore();
  const { mainTab, searchTerm, selectedSort } = supportJobQueueService;
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const debouncedSearch = useDebounce(localSearch, 500);

  const [localPercentage, setLocalPercentage] = useState(
    supportJobQueueService.percentage,
  );
  const debouncedPercentage = useDebounce(localPercentage, 500);

  // Sync debounced search to store
  useEffect(() => {
    supportJobQueueService.setSearchTerm(debouncedSearch);
  }, [debouncedSearch, supportJobQueueService]);

  // Sync debounced percentage to store
  useEffect(() => {
    supportJobQueueService.setPercentage(debouncedPercentage);
  }, [debouncedPercentage, supportJobQueueService]);

  const handleMainTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      supportJobQueueService.setMainTab(newValue);
    },
    [supportJobQueueService],
  );

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
  }, []);

  const handlePercentageChange = useCallback((value: string) => {
    setLocalPercentage(value);
  }, []);

  const handleSortClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleSortClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSortSelect = useCallback(
    (sort: string) => {
      supportJobQueueService.setSelectedSort(sort);
      setAnchorEl(null);
    },
    [supportJobQueueService],
  );

  return (
    <Box className="main-tabs-container">
      <Tabs
        value={mainTab}
        onChange={handleMainTabChange}
        className="main-tabs"
        TabIndicatorProps={{
          className: 'tab-indicator',
        }}>
        <Tab label="Support Queue" className="main-tab" />
        <Tab label="Team Lead Queue" className="main-tab" />
      </Tabs>
      <Box className="header-actions">
        <TextField
          placeholder="Search"
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="search-field"
          size="small"
        />
        <IconButton className="sort-button" onClick={handleSortClick}>
          <img src={UpAndDownFilterIcon} alt="Sort" />
        </IconButton>
        <SortMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleSortClose}
          selectedSort={selectedSort}
          onSelect={handleSortSelect}
        />
        <Box className="percentage-input-container">
          <TextField
            value={localPercentage}
            onChange={(e) => handlePercentageChange(e.target.value)}
            className="percentage-input"
            placeholder="0"
            size="small"
            InputProps={{
              endAdornment: (
                <Box
                  component="span"
                  sx={{ color: '#5E6C84', fontSize: '13px', width: '20px' }}>
                  %
                </Box>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenAddJob(true)}
          sx={{
            '&:hover': { bgcolor: '#374151', boxShadow: 'none' },
            bgcolor: '#111827',
            borderRadius: '4px',
            boxShadow: 'none',
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            height: '32px',
            minWidth: '90px',
            ml: 2,
            textTransform: 'none',
          }}>
          + Add Job
        </Button>
      </Box>
      <AddAndEditJobV2
        open={openAddJob}
        onClose={() => setOpenAddJob(false)}
        scroll="paper"
      />
    </Box>
  );
};

export default observer(JobQueueHeader);
